import { TAdaptedParagraphV2 } from '../../types/types';

/**
 * Regex matching the start of a top-level (non-indented) list item:
 *   - Unordered: `- `, `* `, `+ `
 *   - Ordered:   `1. `, `2. `, etc.
 */
const TOP_LEVEL_LIST_MARKER = /^(?:\d+\.\s|[-*+]\s)/;

/**
 * Split a single text block that contains multiple top-level list items
 * into individual items. Sub-items (indented lines) stay with their parent.
 *
 * Example input:
 *   "- Item A\n  - sub\n- Item B\n- Item C"
 *
 * Output:
 *   ["- Item A\n  - sub", "- Item B", "- Item C"]
 */
export function splitListText(text: string): string[] {
  const lines = text.split('\n');

  const hasMultipleTopLevelItems =
    lines.filter(l => TOP_LEVEL_LIST_MARKER.test(l)).length > 1;

  if (!hasMultipleTopLevelItems) return [text];

  const items: string[] = [];
  let current = '';

  for (const line of lines) {
    const isTopLevel = TOP_LEVEL_LIST_MARKER.test(line);

    if (isTopLevel && current) {
      items.push(current);
      current = line;
    } else {
      current += (current ? '\n' : '') + line;
    }
  }
  if (current) items.push(current);

  return items.length > 1 ? items : [text];
}

/**
 * Expand list paragraphs into individual list-item paragraphs so each
 * top-level item gets its own reference processing chunk.
 *
 * Non-list paragraphs pass through unchanged.
 */
export function expandListParagraphs(
  paragraphs: TAdaptedParagraphV2[]
): TAdaptedParagraphV2[] {
  const expanded: TAdaptedParagraphV2[] = [];

  for (const para of paragraphs) {
    const items = splitListText(para.text.trim());

    if (items.length <= 1) {
      expanded.push(para);
      continue;
    }

    for (const item of items) {
      expanded.push({
        text: item,
        references: [],
      });
    }
  }

  return expanded;
}
